import React, { useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import CryptoJS from "crypto-js";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Loader2,
  Lock,
  ShieldCheck,
  User,
  Wallet,
} from "lucide-react";


const CYBERSOURCE_URL =
  "https://testsecureacceptance.cybersource.com/pay";


export default function PaymentConfirmation({
  paymentData,
  transactionUuid,
  referenceNumber,
  onBack,
}) {

  const [processing, setProcessing] =
    useState(false);

  const [error, setError] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | IMPORTANT
  |--------------------------------------------------------------------------
  |
  | Use the data received from StudentPayment.
  |
  */

  const payment =
    paymentData || {};


  /*
  |--------------------------------------------------------------------------
  | Amount
  |--------------------------------------------------------------------------
  */

  const formattedAmount =
    Number(payment.amount || 0)
      .toFixed(2);


  /*
  |--------------------------------------------------------------------------
  | Confirm Payment
  |--------------------------------------------------------------------------
  */

  const handleConfirmPayment = async () => {
	  if (processing) return;

	  setProcessing(true);
	  setError("");

	  try {
		/*
		|--------------------------------------------------------------------------
		| Call /ucb-api
		|--------------------------------------------------------------------------
		| Send:
		|   matric_no
		|   transaction_uuid
		|   reference_number
		|   amount
		|--------------------------------------------------------------------------
		*/

		const api = axios.create({
		  baseURL: import.meta.env.VITE_API_BASE_URL,
		  headers: {
			"Content-Type": "application/json",
		  },
		});

		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			matric_no: payment.matric_no,
			transaction_uuid: transactionUuid,
			reference_number: referenceNumber,
			amount: payment.amount,
		};

		console.log("UCB API Payload:", payload);

		const response = await api.post(
		  "/ucb-api",
		  payload
		);

		const apiResponse = response.data;

		console.log(
		  "UCB API Response:",
		  apiResponse
		);


		/*
		|--------------------------------------------------------------------------
		| Get HMAC / SECRET KEY
		|--------------------------------------------------------------------------
		*/

		const data =
		  apiResponse?.data ||
		  apiResponse;

		const hmacAlgorithm =
		  data?.HMAC_SHA256 ||
		  data?.hmac_sha256 ||
		  data?.algorithm ||
		  "sha256";

		const secretKey =
		  data?.SECRET_KEY ||
		  data?.secret_key ||
		  data?.secretKey;


		if (!secretKey) {
		  throw new Error(
			"SECRET_KEY was not returned from Server."
		  );
		}


		/*
		|--------------------------------------------------------------------------
		| CyberSource signed fields
		|--------------------------------------------------------------------------
		*/

		const signedFieldNames =
		  [
			"access_key",
			"profile_id",
			"transaction_uuid",
			"signed_field_names",
			"unsigned_field_names",
			"signed_date_time",
			"locale",
			"transaction_type",
			"reference_number",
			"amount",
			"currency",
			"auth_trans_ref_no",
			"bill_to_forename",
			"bill_to_surname",
			"bill_to_email",
			"bill_to_address_postal_code",
			"bill_to_address_country",
			"bill_to_address_city",
			"bill_to_address_line1",
			"bill_to_address_state",
		  ].join(",");


		/*
		|--------------------------------------------------------------------------
		| CyberSource Parameters
		|--------------------------------------------------------------------------
		*/

		const params = {
		  access_key:
			import.meta.env.VITE_CYBERSOURCE_ACCESS_KEY,

		  profile_id:
			import.meta.env.VITE_CYBERSOURCE_PROFILE_ID,

		  transaction_uuid:
			transactionUuid,

		  signed_field_names:
			signedFieldNames,

		  unsigned_field_names:
			"",

		  signed_date_time:
			new Date()
			  .toISOString()
			  .replace(/\.\d{3}Z$/, "Z"),

		  locale:
			"en-us",

		  transaction_type:
			"sale",

		  reference_number:
			referenceNumber,

		  amount:
			Number(payment.amount).toFixed(2),

		  currency:
			"BDT",

		  auth_trans_ref_no:
			referenceNumber,

		  bill_to_forename:
			payment.bill_to_forename,

		  bill_to_surname:
			payment.bill_to_surname,

		  bill_to_email:
			payment.bill_to_email,

		  bill_to_address_postal_code:
			payment.bill_to_address_postal_code,

		  bill_to_address_country:
			payment.bill_to_address_country,

		  bill_to_address_city:
			payment.bill_to_address_city,

		  bill_to_address_line1:
			payment.bill_to_address_line1,

		  bill_to_address_state:
			payment.bill_to_address_state,
		};


		/*
		|--------------------------------------------------------------------------
		| Build Data To Sign
		|--------------------------------------------------------------------------
		*/

		const dataToSign =
		  signedFieldNames
			.split(",")
			.map((field) => {
			  return `${field}=${params[field] ?? ""}`;
			})
			.join(",");


		console.log(
		  "Data To Sign:",
		  dataToSign
		);


		/*
		|--------------------------------------------------------------------------
		| HMAC SHA256
		|--------------------------------------------------------------------------
		*/

		if (
		  String(hmacAlgorithm).toLowerCase() !==
		  "sha256"
		) {
		  throw new Error(
			`Unsupported HMAC algorithm: ${hmacAlgorithm}`
		  );
		}


		const signature =
		  CryptoJS
			.HmacSHA256(
			  dataToSign,
			  secretKey
			)
			.toString(
			  CryptoJS.enc.Base64
			);


		console.log(
		  "CyberSource Signature:",
		  signature
		);


		/*
		|--------------------------------------------------------------------------
		| Add Signature
		|--------------------------------------------------------------------------
		*/

		params.signature =
		  signature;


		/*
		|--------------------------------------------------------------------------
		| Submit to CyberSource
		|--------------------------------------------------------------------------
		*/

		const paymentForm =
		  document.createElement("form");

		paymentForm.method =
		  "POST";

		paymentForm.action = CYBERSOURCE_URL;

		paymentForm.style.display =
		  "none";


		Object.entries(params).forEach(
		  ([name, value]) => {

			const input =
			  document.createElement("input");

			input.type =
			  "hidden";

			input.name =
			  name;

			input.value =
			  value ?? "";

			paymentForm.appendChild(
			  input
			);

		  }
		);


		document.body.appendChild(
		  paymentForm
		);


		paymentForm.submit();

	  } catch (error) {

		console.error(
		  "Payment Error:",
		  error
		);

		setError(
		  error?.response?.data?.message ||
		  error?.response?.data?.responseMsg ||
		  error?.message ||
		  "Unable to process payment."
		);

		setProcessing(false);
	  }
	};

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">

      {/* HEADER */}

      <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-600 to-violet-600 px-6 py-7 text-white sm:px-8">

        <div className="absolute -right-20 -top-28 h-64 w-64 rounded-full bg-white/10" />

        <div className="relative z-10 flex items-center gap-4">

          <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15">

            <CheckCircle2
              size={28}
            />

          </div>

          <div>

            <h2 className="text-xl font-bold sm:text-2xl">
              Review Payment Details
            </h2>

            <p className="mt-1 text-sm text-blue-100">
              Please verify all information before confirming your payment.
            </p>

          </div>

        </div>

      </div>


      {/* BODY */}

      <div className="p-5 sm:p-8">


        {error && (

          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {error}
          </div>

        )}


        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">


          {/* STUDENT */}

          <ReviewSection
            icon={<User size={18} />}
            title="Student Information"
          >

            <ReviewRow
              label="Matric No"
              value={payment.matric_no}
            />

            <ReviewRow
              label="Registration No"
              value={payment.registration_no}
            />

            <ReviewRow
              label="Name"
              value={payment.name}
            />

            <ReviewRow
              label="Department"
              value={payment.department}
            />

            <ReviewRow
              label="Programme"
              value={payment.programme}
            />

            <ReviewRow
              label="Admission"
              value={payment.admission}
            />

            <ReviewRow
              label="Payment Amount"
              value={`৳ ${formattedAmount}`}
            />

          </ReviewSection>


          {/* BILLING */}

          <ReviewSection
            icon={<CreditCard size={18} />}
            title="Billing Information"
          >

            <ReviewRow
              label="First Name"
              value={
                payment.bill_to_forename
              }
            />

            <ReviewRow
              label="Last Name"
              value={
                payment.bill_to_surname
              }
            />

            <ReviewRow
              label="Email"
              value={
                payment.bill_to_email
              }
            />

            <ReviewRow
              label="Address"
              value={
                payment.bill_to_address_line1
              }
            />

            <ReviewRow
              label="City"
              value={
                payment.bill_to_address_city
              }
            />

            <ReviewRow
              label="State"
              value={
                payment.bill_to_address_state
              }
            />

            <ReviewRow
              label="Postal Code"
              value={
                payment.bill_to_address_postal_code
              }
            />

            <ReviewRow
              label="Country"
              value={
                payment.bill_to_address_country
              }
            />

          </ReviewSection>

        </div>


        {/* AMOUNT */}

        <motion.div
          animate={{
            boxShadow: [
              "0 0 0 rgba(34,197,94,0)",
              "0 8px 25px rgba(34,197,94,.08)",
              "0 0 0 rgba(34,197,94,0)",
            ],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
          }}
          className="mt-6 flex items-center justify-between rounded-2xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-5"
        >

          <div className="flex items-center gap-3">

            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-100 text-green-700">

              <Wallet
                size={21}
              />

            </div>

            <div>

              <p className="text-sm font-semibold text-green-800">
                Total Payment Amount
              </p>

              <p className="text-xs text-green-600">
                Payable amount
              </p>

            </div>

          </div>


          <div className="text-2xl font-extrabold text-green-700 sm:text-3xl">

            ৳ {formattedAmount}

          </div>

        </motion.div>


        {/* SECURITY */}

        <div className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-center text-xs text-slate-500">

          <Lock
            size={14}
            className="text-green-600"
          />

          Please make sure all information is correct.
          Your payment will be processed securely.

        </div>


        {/* ACTIONS */}

        <div className="mt-7 flex flex-col-reverse items-center justify-center gap-3 border-t border-slate-200 pt-7 sm:flex-row">


          {/* BACK */}

          <button
            type="button"
            onClick={onBack}
            disabled={processing}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >

            <ArrowLeft
              size={17}
            />

            Back & Edit

          </button>


          {/* CONFIRM */}

          <motion.button
            type="button"
            onClick={
              handleConfirmPayment
            }
            disabled={
              processing
            }

            whileHover={
              !processing
                ? { y: -3 }
                : {}
            }

            whileTap={
              !processing
                ? { scale: 0.97 }
                : {}
            }

            className="flex h-12 w-full min-w-[220px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 px-7 font-bold text-white shadow-lg shadow-green-500/20 transition disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          >

            {processing ? (

              <>

                <Loader2
                  size={18}
                  className="animate-spin"
                />

                Processing Payment...

              </>

            ) : (

              <>

                <ShieldCheck
                  size={18}
                />

                Confirm & Pay

                <ArrowRight
                  size={18}
                />

              </>

            )}

          </motion.button>

        </div>

      </div>

    </div>
  );
}


/*
|--------------------------------------------------------------------------
| Review Section
|--------------------------------------------------------------------------
*/

function ReviewSection({
  icon,
  title,
  children,
}) {

  return (

    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 transition duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6">

      <div className="mb-2 flex items-center gap-3 border-b border-slate-200 pb-4">

        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          {icon}
        </div>

        <h3 className="font-bold text-slate-800">
          {title}
        </h3>

      </div>

      {children}

    </div>

  );
}


/*
|--------------------------------------------------------------------------
| Review Row
|--------------------------------------------------------------------------
*/

function ReviewRow({
  label,
  value,
}) {

  return (

    <div className="group flex min-h-[48px] items-center border-b border-slate-200 last:border-b-0 transition hover:bg-white">

      <div className="w-[42%] shrink-0 text-xs font-semibold text-slate-500 sm:text-sm">
        {label}
      </div>

      <div className="w-[58%] break-words text-sm font-semibold text-slate-800 transition group-hover:text-blue-600">
        {value || "-"}
      </div>

    </div>

  );
}